import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Message, Spin } from "../../BP2Components";
import { getFormWithData, getQuestionnaire } from "../../../services/restAPI/FormsV3";
import { generateGUID } from "../../../util/Scripts";
import FormV3Component from "./FormV3Component";
import { useFormV3 } from "../../../context/FormV3Context/FormV3Context";
import "./formsV3.less";
import { useIntl } from "react-intl";

/**
 * Allows to use the form component in any place of the project
 * @param {string} [customGUID] - Optional parameter to pass a custom formController ID
 * @param {string} formId - Form structure id
 * @param {string} [dataId] - Exact record id of data showed in form, only structure is returned when no dataId is passed
 * @param {object} [initialValuesObject] - Allows to set initial values to certain fields: {fieldId:fieldValue, ...}
 * @param {object} [printable] - Show print button, true/false
 * @param {function()} [saveCallback] - Allows to inform about save event and return any data
 * @param {onStatusChange} [getFormCallback] - Allows to return getFormData to the parent component
 * @returns React component
 */
const FormV3Controller = ({
   customGUID,
   formId,
   dataId,
   initialValuesObject,
   printable,
   saveCallback,
   preparedFormResponse,
   setHeader,
   pocData,
   onStatusChange,
   isQuestionnaire,
}) => {
   const intl = useIntl();
   const { addNewFormV3Data } = useFormV3();
   const [formResponseData, setFormResponseData] = useState();
   const [formGUID, setFormGUID] = useState("");
   const [formFields, setFormFields] = useState([]);
   const [formLoading, setFormLoading] = useState(false);
   const [fetchError, setFetchError] = useState(false);

   const fetchErrorMessage = intl.formatMessage({ id: "forms.errorWhileFetchingData" });
   const isFormInModal = !!setHeader;

   const prepareFormFieldList = useCallback(
      (fields) => {
         const preparedFields = [];
         if (!Array.isArray(fields)) return preparedFields;

         fields.forEach((field) => {
            if (field?.value == null) {
               preparedFields.push(field);
               return;
            }

            const updatedField = {
               ...field,
               value: Array.isArray(field?.value)
                  ? [...field.value]
                  : typeof field?.value === "object" && field.value !== null
                    ? { ...field.value }
                    : (field?.value ?? null),
            };

            const hasInitial = initialValuesObject && initialValuesObject[updatedField.id] != null;

            if (hasInitial) {
               updatedField.value = initialValuesObject[updatedField.id];
            } else if (updatedField.value != null && field?.type !== "Select") {
               const originalValue = updatedField?.value?.originalValue;
               if (Array.isArray(originalValue)) {
                  updatedField.value = originalValue[0] ?? "";
               }
            }
            preparedFields.push(updatedField);
         });
         return preparedFields;
      },
      [initialValuesObject],
   );

   const applyResponse = useCallback(
      (response, formControllerId, GUID) => {
         setFormResponseData(response);
         setHeader?.(response.name);
         const preparedFields = prepareFormFieldList(response?.fields);
         setFormFields(preparedFields);
         const newControllerData = {};
         newControllerData[formControllerId] = {};
         preparedFields.forEach((field) => {
            newControllerData[formControllerId][field.id] = field.value;
         });
         newControllerData[formControllerId].formId = formId;
         addNewFormV3Data(newControllerData, response, GUID);
      },
      [formId, setHeader, prepareFormFieldList, addNewFormV3Data],
   );

   useEffect(() => {
      if (!formId && (!pocData || Object.keys(pocData).length === 0)) return;
      let isCancelled = false;

      const formControllerId = customGUID ?? generateGUID("form");
      setFormGUID(formControllerId);

      if (pocData && Object.keys(pocData).length > 0) {
         setFormLoading(false);
         onStatusChange?.("success");
         applyResponse(pocData, formControllerId, formControllerId);
         return;
      }

      if (!formId) return;

      if (preparedFormResponse) {
         setFormLoading(false);
         onStatusChange?.("success");
         applyResponse(preparedFormResponse, formControllerId, formControllerId);
         return;
      }

      setFormLoading(true);
      setFetchError(false);
      onStatusChange?.("loading");

      const trimmedFormId = String(formId).trim();
      const trimmedDataId = dataId != null ? String(dataId).trim() : undefined;

      const handleFetchError = () => {
         setFormLoading(false);
         setFetchError(true);
         onStatusChange?.("error");
         Message.error(intl.formatMessage({ id: "forms.errorWhileFetchingData" }));
      };

      if (!trimmedFormId) {
         handleFetchError();
         return;
      }

      if (!isQuestionnaire && trimmedFormId.length <= 10) {
         handleFetchError();
         return;
      }

      const request = isQuestionnaire
         ? getQuestionnaire(trimmedFormId)
         : getFormWithData(trimmedFormId, trimmedDataId);

      if (!request) {
         handleFetchError();
         return;
      }

      request
         .then((res) => {
            if (isCancelled) return;
            applyResponse(res.data, formControllerId, formControllerId);
            onStatusChange?.("success");
            setFormLoading(false);
         })
         .catch(() => {
            if (isCancelled) return;
            handleFetchError();
         });

      return () => {
         isCancelled = true;
      };
   }, [dataId, formId, isQuestionnaire, pocData?.id]);

   return (
      <div
         className={clsx("formV3-container", isQuestionnaire && "formV3-container--questionnaire")}
         id={`form-${formGUID}`}
      >
         {formLoading ? (
            <Spin></Spin>
         ) : fetchError ? (
            <div className="formV3-error">{fetchErrorMessage}</div>
         ) : formResponseData && formFields ? (
            <FormV3Component
               formGUID={formGUID}
               formResponseData={formResponseData}
               dataId={dataId}
               formId={isQuestionnaire ? formResponseData.id : (formId ?? pocData?.id)}
               formFields={formFields}
               printable={printable}
               saveCallback={saveCallback}
               isFormInModal={isFormInModal}
               isQuestionnaire={isQuestionnaire}
            />
         ) : null}
      </div>
   );
};

export default FormV3Controller;
